import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import lightFormat from "date-fns/lightFormat";
import parse from "date-fns/parse";
import { DELIMITER_REGEX } from "../types";
import { getToday, toDate, toDatePickerDate } from "../util/calendar";

// Standard keys we use to track validation throughout the DatePickers.

/**
 * Lets adjust the format to match what date-fns wants:
 * https://date-fns.org/v2.23.0/docs/lightFormat
 * @param format
 * @returns string for date-fns format
 */
export function fixFormat(format) {
  return format.toLowerCase().replace("mm", "MM");
}

/**
 *
 * @param value
 * @param format
 * @returns date string
 */
export function dateToInputString(value, format) {
  const date = value ? toDate(value) : getToday();
  const returnString = lightFormat(date, fixFormat(format));
  return returnString;
}

// For now, lock the reference date to 2023 so we can have consistent date parsing for now in case
// our devs tests are relying on this behavior. In a future release, we can remove this and just use
// new Date() as the reference date since that is what our end users will likely expect. After that,
// something like (thisYear +/- 50) will return the nearest century whereas right now we're locking
// it to 2023 +/- 50.
//
// See the tests for this function for more information.
const referenceDateThatWillReturnThe20thCenturyForBackCompat = new Date(2023, 11, 31);
/**
 * Given a *valid* input string, this converts it to the DatePickerDate object
 * @param value
 * @param format
 * @returns DatePickerDate
 */
export function inputStringToDate(value, format) {
  const date = parse(value, fixFormat(format), referenceDateThatWillReturnThe20thCenturyForBackCompat);
  return toDatePickerDate(date);
}

/**
 * This is just to be sure the input is in the right format and valid.
 * @param value
 * @param format
 * @returns string
 */
export const validateInput = (value, format, minDate, maxDate, dayIsDisabled, isRequired) => {
  if (isRequired && value.length === 0) {
    return "isRequired";
  } else if (!validateFormat(value, format)) {
    return "isInvalid";
  } else {
    let returnValue = "isValid";
    const testDate = parse(value, fixFormat(format), new Date());
    if (testDate.toString() === "Invalid Date") {
      returnValue = "isInvalid";
    }
    if ((minDate || maxDate) && returnValue === "isValid") {
      returnValue = isDateBetweenDates(testDate, minDate, maxDate);
    }
    if (dayIsDisabled) {
      const isDisabled = dayIsDisabled(inputStringToDate(value, format));
      if (isDisabled) {
        returnValue = "isDisabled";
      }
    }
    return returnValue;
  }
};

/**
 * This converts the format to regex and tests against it. It'll aco
 * @param value
 * @param format
 * @returns boolean
 */
export function validateFormat(value, format) {
  const regexString = format.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&").replace(/m|d|y/gi, "\\d");
  return new RegExp(regexString).test(value);
}

/**
 *
 * @param value
 * @param format
 * @returns A value with the delimiter inserted if appropriate
 */
export function parseValue(value, format) {
  const nextChar = format[value.length];
  if (nextChar && isDelimiter(nextChar)) {
    const remainingFormat = format.substring(value.length, format.length);

    //Need to check all characters as some delimiters are > 1 character.
    for (let i = 0; i < remainingFormat.length; i++) {
      if (!isDelimiter(remainingFormat.charAt(i))) break;
      value += remainingFormat.charAt(i);
    }
  }
  return value;
}
/**
 * Function to check whether the given character is a delimiter character.
 * @param char
 * @returns boolean
 * */
function isDelimiter(char) {
  return char.match(DELIMITER_REGEX) !== null;
}

/**
 * Checks if a date is between dates, both min and max are optional.
 * If only one is provided, it will check if the date is before the min, or after the max
 * @param date
 * @param minDate
 * @param maxDate
 * @returns string
 */
export function isDateBetweenDates(date, minDate, maxDate) {
  if (!minDate && !maxDate) {
    return "isValid";
  }
  const testDate = date instanceof Date ? date : toDate(date);
  if (minDate) {
    const min = minDate instanceof Date ? minDate : toDate(minDate);
    if (isBefore(testDate, min)) {
      return "isBefore";
    }
  }
  if (maxDate) {
    const max = maxDate instanceof Date ? maxDate : toDate(maxDate);
    if (isAfter(testDate, max)) {
      return "isAfter";
    }
  }
  return "isValid";
}

/**
 * A helper to auto-insert delimiters and zeroes into the user-typed value
 * @param value
 * @param format
 * @param delimiter
 * @returns new value
 */
export function inputHelper(value, format) {
  let parsedValue = parseValue(value, format);
  const doubleDRegex = new RegExp(/([-./年月日]|(\. )){2}/);
  const doubleDindex = parsedValue.match(doubleDRegex)?.index;
  if (doubleDindex) {
    const repeatedChar = parsedValue.charAt(doubleDindex);
    if (format.charAt(doubleDindex) === repeatedChar) {
      parsedValue = parsedValue.replace(`${repeatedChar}${repeatedChar}`, repeatedChar);
    } else if (format.charAt(doubleDindex + 1)) {
      const start = parsedValue.substring(0, doubleDindex - 1);
      const fix = "0" + parsedValue.charAt(doubleDindex - 1);
      const end = parsedValue.substring(doubleDindex + 1);
      parsedValue = `${start}${fix}${end}`;
    }
  }
  return parsedValue;
}
//# sourceMappingURL=utils.js.map